import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { rawCacaClient } from '@plangrid-private/web-api-clients';
import styled from 'styled-components';

import * as analytics from '@adsk/acs-web-analytics';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import { Box } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { OpenIcon } from '@adsk/alloy-react-icon';
import InlineEdit from '@adsk/alloy-react-inline-edit';
import { useLocale } from '@adsk/alloy-react-locale';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import Panel from '@adsk/alloy-react-panel';

import FormsAssigneePicker from './FormsAssigneePicker';
import FormsDatePicker from './FormsDatePicker';
import { useFormsContext } from './FormsProvider';
import {
  FORM_COLLABORATION_MODES,
  FORM_PROPERTY_KEYS,
  FORM_TEMPLATE_PERMISSIONS,
  FORM_ANALYTICS,
  FORM_ANALYTICS_CONTRIBUTION_MAP,
  ASSIGNEE_TYPES,
  FEATURE_FLAGS,
} from './helpers/constants';
import { ACTIONS } from './helpers/reducers/forms';
import {
  useCanUserCreateForm,
  useUserPermissionInfo,
  useUsersByOxygenIdMap,
} from './hooks';
import {
  useCompaniesMapById,
  useRolesMapById,
  useUserPermissionsByPgIdMap,
  useUsersByIdMap,
} from './hooks/useEntitiesMap';
import { useShouldShowAssigneePicker } from './hooks/useShouldShowAssigneePicker';
import { usePlangridFeatureFlag } from './PlangridLaunchDarklyProvider';
import UnstyledAnchor from '../../UnstyledAnchor';
import {
  getFormDetailsUrl,
  getCreateFormUrl,
  getFormAssignee,
  isFormReadOnly,
  isUserInCompany,
  isUserInRole,
  getUTCDate,
} from './helpers/utils';

const StyledForm = styled.form`
  width: 100%;
  overflow: hidden;
  padding-bottom: 10px;
`;

const FormsCreateNewEntityPanel = ({
  onClose,
  open,
  floatingPanel,
  onCreateSuccess,
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const { value: isStatusExpansionEnabled } = usePlangridFeatureFlag(
    FEATURE_FLAGS.STATUS_EXPANSION,
    false,
  );
  const {
    newForm,
    newFormTemplate,
    pgProjectUser,
    pgProjectUsers,
    isLoadingPgProjectUsers,
    forgeUserMapping: forgeUserMappingByPGID,
    isUpdateRequestInFlight,
    dispatch,
  } = useFormsContext();
  const { data: usersMapByOxygenID, loading: isLoadingUsersMapByOxygenId } =
    useUsersByOxygenIdMap();
  const { data: usersMapByID, loading: isLoadingUsersMapById } =
    useUsersByIdMap();
  const {
    data: userPermissionsMapByPGID,
    loading: loadingUserPermissionsMapByPGID,
  } = useUserPermissionsByPgIdMap();
  const { data: companiesMapByID, loading: isLoadingCompaniesMap } =
    useCompaniesMapById();
  const { data: rolesMapByID, loading: isLoadingRolesMap } = useRolesMapById();
  const canUserCreateForm = useCanUserCreateForm();

  const { data: userPermissionInfo, loading: isLoadingPermissionInfo } =
    useUserPermissionInfo(pgProjectUser?.user_id || '');
  const projectId = useCurrentProjectId();
  const [showModal, setShowModal] = useState(false);
  const [stagedAssigneeValue, setStagedAssigneeValue] = useState(null);

  const forgeUserMappingByOxygenID = useMemo(() => {
    const forgeMappingByOxygenID = {};
    for (const [PG, Oxygen] of Object.entries(forgeUserMappingByPGID)) {
      forgeMappingByOxygenID[Oxygen] = PG;
    }

    return forgeMappingByOxygenID;
  }, [forgeUserMappingByPGID]);

  const assigneeValue = useMemo(() => {
    if (
      loadingUserPermissionsMapByPGID ||
      isLoadingUsersMapByOxygenId ||
      !newForm
    )
      return null;

    const assigneeId = getFormAssignee(newForm);

    if (!assigneeId) return null;

    const assigneeType = newForm.assignee_type;
    if (assigneeType === ASSIGNEE_TYPES.COMPANY) {
      return {
        value: assigneeId,
        group: ASSIGNEE_TYPES.COMPANY,
      };
    } else if (assigneeType === ASSIGNEE_TYPES.ROLE) {
      return {
        value: assigneeId,
        group: ASSIGNEE_TYPES.ROLE,
      };
    } else {
      const oxygenId = forgeUserMappingByPGID[assigneeId];
      const user = oxygenId && usersMapByOxygenID[oxygenId];

      return user
        ? {
            value: user.id,
            group: ASSIGNEE_TYPES.USER,
          }
        : null;
    }
  }, [
    forgeUserMappingByPGID,
    loadingUserPermissionsMapByPGID,
    newForm,
    usersMapByOxygenID,
    isLoadingUsersMapByOxygenId,
  ]);

  useEffect(() => {
    if (!isLoadingPgProjectUsers && newFormTemplate && open) {
      analytics.track(FORM_ANALYTICS.CREATE, {
        source: 'reference_picker',
        report_contribution_type:
          FORM_ANALYTICS_CONTRIBUTION_MAP[
            newFormTemplate.formCollaborationMode
          ],
        num_of_assignees:
          newFormTemplate.formCollaborationMode ===
          FORM_COLLABORATION_MODES.MULTIPLE_PARALLEL
            ? pgProjectUsers.filter(({ user_id }) => {
                return canUserCreateForm(
                  userPermissionsMapByPGID[user_id],
                  newFormTemplate,
                );
              }).length
            : 1,
      });
    }
  }, [
    isLoadingPgProjectUsers,
    pgProjectUsers,
    userPermissionsMapByPGID,
    newFormTemplate,
    open,
    canUserCreateForm,
  ]);

  const areFieldsDisabled = useMemo(() => {
    if (isLoadingPermissionInfo || isUpdateRequestInFlight) return true;

    return isFormReadOnly(
      userPermissionInfo,
      newForm,
      newFormTemplate,
      isStatusExpansionEnabled,
    );
  }, [
    isLoadingPermissionInfo,
    userPermissionInfo,
    newForm,
    newFormTemplate,
    isUpdateRequestInFlight,
    isStatusExpansionEnabled,
  ]);

  const updateForm = useCallback(
    (updates) => {
      dispatch({
        type: ACTIONS.UPDATE_FORM_START,
      });

      // TODO: FORMS-6571 Add route to @plangrid-private/web-api-clients
      rawCacaClient
        .put(getCreateFormUrl(projectId, newForm.type, newForm.uid), updates)
        .then((form) => {
          dispatch({
            type: ACTIONS.UPDATE_FORM_COMPLETE,
            payload: {
              newForm: {
                ...form,
                id: form.uid,
              },
            },
          });
        })
        .catch(() => {
          dispatch({ type: ACTIONS.UPDATE_FORM_ERROR });
        });
    },
    [dispatch, newForm, projectId],
  );

  const handleModalClose = () => {
    setStagedAssigneeValue(null);
    setShowModal(false);
  };

  const handleModalConfirm = () => {
    updateForm(stagedAssigneeValue);
    setShowModal(false);
  };

  const handleAssigneeChange = useCallback(
    ({ value, group }) => {
      let assignee_id;
      let assignee_type;

      if (group === ASSIGNEE_TYPES.USER) {
        const user = usersMapByID[value] || {};
        assignee_id = user ? forgeUserMappingByOxygenID[user.autodeskId] : null;
        assignee_type = null;
      } else {
        assignee_id = value;
        assignee_type = group;
      }

      if (
        !isUserInCompany(userPermissionInfo, assignee_id) &&
        !isUserInRole(userPermissionInfo, assignee_id) &&
        userPermissionInfo?.userPGID !== assignee_id
      ) {
        setStagedAssigneeValue({
          [FORM_PROPERTY_KEYS.ASSIGNEE]: assignee_id,
          [FORM_PROPERTY_KEYS.ASSIGNEE_TYPE]: assignee_type,
        });
        setShowModal(true);
      } else {
        setStagedAssigneeValue(null);
        updateForm({
          [FORM_PROPERTY_KEYS.ASSIGNEE]: assignee_id,
          [FORM_PROPERTY_KEYS.ASSIGNEE_TYPE]: assignee_type,
        });
      }
    },
    [userPermissionInfo, forgeUserMappingByOxygenID, usersMapByID, updateForm],
  );

  const renderAssigneeValue = useCallback(
    ({ group, value }) => {
      if (isLoadingCompaniesMap || isLoadingRolesMap || isLoadingUsersMapById)
        return null;

      switch (group) {
        case ASSIGNEE_TYPES.USER:
          const oxygenUser = usersMapByID[value];
          return oxygenUser ? oxygenUser.name : null;
        case ASSIGNEE_TYPES.COMPANY:
          return companiesMapByID[value]?.name;
        case ASSIGNEE_TYPES.ROLE:
          return rolesMapByID[value]?.name;
        default:
          return null;
      }
    },
    [
      usersMapByID,
      companiesMapByID,
      rolesMapByID,
      isLoadingCompaniesMap,
      isLoadingRolesMap,
      isLoadingUsersMapById,
    ],
  );

  const assigneeOptionsFilter = loadingUserPermissionsMapByPGID
    ? {}
    : {
        user: ({ autodeskId }) => {
          const userPGID = forgeUserMappingByOxygenID[autodeskId];

          return canUserCreateForm(
            userPermissionsMapByPGID[userPGID],
            newFormTemplate,
          );
        },
        company: ({ id }) => {
          const companyPermissions = newFormTemplate.companies
            ? newFormTemplate.companies.find(
                ({ company_id }) => company_id === id,
              )
            : [];

          return (
            companyPermissions &&
            companyPermissions.permissions.includes(
              FORM_TEMPLATE_PERMISSIONS.SUBMIT,
            )
          );
        },
        role: ({ id }) => {
          const rolePermissions = newFormTemplate.roles
            ? newFormTemplate.roles.find(({ role_id }) => role_id === id)
            : [];

          return (
            rolePermissions &&
            rolePermissions.permissions.includes(
              FORM_TEMPLATE_PERMISSIONS.SUBMIT,
            )
          );
        },
      };

  const showAssigneePicker = useShouldShowAssigneePicker(
    newFormTemplate,
    isStatusExpansionEnabled,
  );

  return (
    <Panel
      open={open}
      floating={floatingPanel}
      onClose={onClose}
      title={`${newFormTemplate.name} #${newForm.report_num}`}
      renderFooter={() => (
        <Box padding="16px">
          <Button
            style={{ width: '100%' }}
            size={Button.SIZES.LARGE}
            variant={Button.VARIANTS.PRIMARY}
            disabled={isUpdateRequestInFlight}
            onClick={() => {
              onCreateSuccess(newForm);
            }}
          >
            {T.translate('ADD_REFERENCE')}
          </Button>
        </Box>
      )}
    >
      <ModalConfirmation
        show={showModal}
        onClose={handleModalClose}
        title={T.translate('REASSIGN_DRAFT')}
        message={T.translate('REASSIGN_MESSAGE')}
        cancelButtonLabel={T.translate('CANCEL')}
        confirmationButtonLabel={T.translate('REASSIGN')}
        onConfirm={handleModalConfirm}
        hideOnConfirm={true}
      />
      <Panel.Body style={{ paddingTop: '10px' }}>
        <Button
          as={UnstyledAnchor}
          renderIcon={(p) => <OpenIcon {...p} />}
          variant={Button.VARIANTS.TERTIARY}
          href={getFormDetailsUrl(projectId, newForm.type, newForm.uid)}
          style={{
            padding: '0px',
            paddingRight: '4px',
            '&:hover': {
              background: 'none',
            },
            '&:focus': {
              background: 'none',
            },
          }}
        >
          {T.translate('OPEN_IN_NEW_TAB')}
        </Button>
        <Divider style={{ marginTop: '10px', marginBottom: '16px' }} />

        <Box paddingBottom="16px" typography="heading3">
          {T.translate('FORM_DETAILS')}
        </Box>
        <Box paddingBottom="10px" typography="labelMedium">
          {T.translate('FORM_DATE')}
        </Box>
        <Box paddingBottom="10px">
          <FormsDatePicker
            disabled={areFieldsDisabled}
            formDate={newForm.report_date}
            onChange={(date) => {
              updateForm({ [FORM_PROPERTY_KEYS.DATE]: getUTCDate(date) });
            }}
          />
        </Box>
        <Box paddingBottom="10px" typography="labelMedium">
          {T.translate('DESCRIPTION')}
        </Box>
        <StyledForm>
          <InlineEdit
            disabled={areFieldsDisabled}
            onChange={(value) =>
              updateForm({ [FORM_PROPERTY_KEYS.DESCRIPTION]: value })
            }
            placeholder={T.translate('ADD_DESCRIPTION')}
            value={newForm[FORM_PROPERTY_KEYS.DESCRIPTION]}
          />
        </StyledForm>
        {showAssigneePicker && (
          <React.Fragment>
            <Box paddingBottom="10px" typography="labelMedium">
              {T.translate('ASSIGNED_TO')}
            </Box>
            <StyledForm>
              <InlineEdit
                disabled={areFieldsDisabled}
                onChange={handleAssigneeChange}
                value={assigneeValue}
                renderValue={renderAssigneeValue}
                renderInput={({ value, onChange }) => (
                  <FormsAssigneePicker
                    defaultIsOpen
                    selection={value}
                    disabled={areFieldsDisabled}
                    optionsOrder={newForm.pg_form ? undefined : ['user']}
                    optionsFilter={assigneeOptionsFilter}
                    onChange={onChange}
                  />
                )}
              />
            </StyledForm>
          </React.Fragment>
        )}
        {newFormTemplate.inc_description && (
          <React.Fragment>
            <Box paddingBottom="10px" typography="labelMedium">
              {T.translate('NOTES')}
            </Box>
            <StyledForm>
              <InlineEdit
                disabled={areFieldsDisabled}
                onChange={(value) =>
                  updateForm({ [FORM_PROPERTY_KEYS.NOTES]: value })
                }
                placeholder={T.translate('ADD_NOTES')}
                value={newForm[FORM_PROPERTY_KEYS.NOTES]}
              />
            </StyledForm>
          </React.Fragment>
        )}
      </Panel.Body>
    </Panel>
  );
};
export default FormsCreateNewEntityPanel;
